import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { PrintButtonHeader } from './2284Tab/2284Dialog/Pdf2284CommonUtils';
import {
  ContinuingCareRecommendation,
  HeaderSection,
  LengthOfTimeAndTimeSpent,
  MappedFieldSection,
  MedicationSection,
  ProgramTransitionalSection,
  SubstanceAndTreatmentSection,
  SucideSection,
} from './ProfA1PdfFormCommonUtils';
import {
  TeatmentAccomplishmentTitle,
  getAddressField,
  getCareSectionData,
  getChildrenFields,
  getEmergency1Fields,
  getEmergency2Fields,
  getMappedFieldData,
  getMarritalstatusFields,
  getMedicineData,
  getParoleFields,
  getParticipantFields,
  getProgramOptCheckbox,
  getProgramTransitionalData,
  getProgramTransitionalTitle,
  getReferralCheckbox,
  getResidentialCheckbox,
  getSucideData,
  substanceUseHistoryData,
  substanceUseHistoryTitle,
  treatmentHistoryData,
  treatmentHistoryTitle,
} from './ProfA1PdfFormConstants';

const ProfA1PdfForm = (props) => {
  const { open, setOpen } = props;
  const profA1Form = useSelector((state) => state?.profA1Form);
  const profA2Form = useSelector((state) => state?.profA2Form);

  const {
    episodeID,
    participant,
    CDCNumber,
    countyofLastLegalResidence,
    paroleRegion,
    SAT,
    CSRAScore,
    ASIScore,
    primaryLanguage,
    maritalStatus,
    highestEducation,
    permanentAddress,
    permanentCity,
    permanentZipCode,
    permanentTelephone,
    veteran,
    ADA,
    usualOccupation,
    years,
    emergencyContact1Name,
    emergencyContact1Address,
    emergencyContact1City,
    emergencyContact1Phone,
    emergencyContact1Relationship,
    emergencyContact2Name,
    emergencyContact2Address,
    emergencyContact2City,
    emergencyContact2Phone,
    emergencyContact2Relationship,
    historyofSuicideAttemptsPsychIssuesReportedbyPart,
    classifiedCCCMS,
    classifiedCCCMSYear = '',
    classifiedCCCMSClearedYear = '',
    medicineName1 = '',
    medicineName2 = '',
    medicineName3 = '',
    medicineCondition1 = '',
    medicineCondition2 = '',
    medicineCondition3 = '',
    medComments = '',
    numberofChildren = '',
    Ages,
    csbCpsInvolved,
    childrenLiveWith,
  } = profA1Form || {};
  const { value: maritalStatusValue } = maritalStatus || {};
  const { participantName, DOB, EthnicGroup } = participant || {};
  const { id: EthnicGroupId, value: EthnicGroupValue } = EthnicGroup || {};
  const { id: countyId, value: countyValue } = countyofLastLegalResidence || {};
  const { id: paroleRegionId, value: paroleRegionValue } = paroleRegion || {};
  const { id: SATId, value: SATValue } = SAT || {};
  const { value: highestEducationValue } = highestEducation || {};

  const {
    drug1Used = {},
    drug1InTake = {},
    drug1Frequency = {},
    drug1AgeFirstUsed = '',
    drug2Used = {},
    drug2InTake = {},
    drug2Frequency = {},
    drug2AgeFirstUsed = '',
    drug3Used = {},
    drug3InTake = {},
    drug3Frequency = {},
    drug3AgeFirstUsed = '',
    drug4Used = {},
    drug4InTake = {},
    drug4Frequency = {},
    drug4AgeFirstUsed = '',
    program1Name = '',
    program2Name = '',
    program3Name = '',
    program1Modality = '',
    program2Modality = '',
    program3Modality = '',
    program1Year = '',
    program2Year = '',
    program3Year = '',
    program1Completed = '',
    program2Completed = '',
    program3Completed = '',
    program1TreatmentLength = '',
    program2TreatmentLength = '',
    program3TreatmentLength = '',
    LengthofTimeSinceClientLastUsed = '',
    TimeSpentinInstitutions = '',
    treatmentAccomplishments = '',
    residential: residentialValue,
    soberLiving,
    outPatient,
    dayTreatment,
    referredForTreatment,
    notReferredInappropriate,
    notReferredRefusingTreatment,
    recommendSoberLivingWithIntensiveOdf,
    FOTEP,
    dateSigned,
  } = profA2Form || {};
  const { value: drug1Name } = drug1Used ?? {};
  const { value: HowUsedDrug1 } = drug1InTake ?? {};
  const { value: FrequencyUsedDrug1 } = drug1Frequency ?? {};
  const { value: drug2Name } = drug2Used ?? {};
  const { value: HowUsedDrug2 } = drug2InTake ?? {};
  const { value: FrequencyUsedDrug2 } = drug2Frequency ?? {};
  const { value: drug3Name } = drug3Used ?? {};
  const { value: HowUsedDrug3 } = drug3InTake ?? {};
  const { value: FrequencyUsedDrug3 } = drug3Frequency ?? {};
  const { value: drug4Name } = drug4Used ?? {};
  const { value: HowUsedDrug4 } = drug4InTake ?? {};
  const { value: FrequencyUsedDrug4 } = drug4Frequency ?? {};

  const handleClose = () => {
    setOpen(false);
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const referral = getReferralCheckbox({
    referredForTreatment,
    notReferredInappropriate,
    notReferredRefusingTreatment,
  });
  const residential = getResidentialCheckbox({
    residentialValue,
    soberLiving,
    outPatient,
    dayTreatment,
  });
  const programOpt = getProgramOptCheckbox({
    recommendSoberLivingWithIntensiveOdf,
    FOTEP,
  });
  const historyData = treatmentHistoryData({
    program1Name,
    program2Name,
    program3Name,
    program1Modality,
    program2Modality,
    program3Modality,
    program1Year,
    program2Year,
    program3Year,
    program1Completed,
    program2Completed,
    program3Completed,
    program1TreatmentLength,
    program2TreatmentLength,
    program3TreatmentLength,
  });
  const historyTitle = treatmentHistoryTitle();
  const substanceData = substanceUseHistoryData({
    drug1Name,
    HowUsedDrug1,
    FrequencyUsedDrug1,
    drug1AgeFirstUsed,
    drug2Name,
    HowUsedDrug2,
    FrequencyUsedDrug2,
    drug2AgeFirstUsed,
    drug3Name,
    HowUsedDrug3,
    FrequencyUsedDrug3,
    drug3AgeFirstUsed,
    drug4Name,
    HowUsedDrug4,
    FrequencyUsedDrug4,
    drug4AgeFirstUsed,
  });
  const substanceTitle = substanceUseHistoryTitle();
  const participantFieldsData = getParticipantFields({
    participantName,
    CDCNumber,
    DOB,
    countyValue,
  });
  const peroleFieldsData = getParoleFields({
    paroleRegionId,
    SATId,
    CSRAScore,
    ASIScore,
  });
  const addressFieldsData = getAddressField({
    permanentAddress,
    permanentCity,
    permanentZipCode,
    permanentTelephone,
  });
  const marritalFieldsData = getMarritalstatusFields({
    maritalStatusValue,
    EthnicGroupValue,
    primaryLanguage,
    highestEducationValue,
    veteran,
    ADA,
    usualOccupation,
    years,
  });
  const childrenFieldsData = getChildrenFields({
    numberofChildren,
    Ages,
    csbCpsInvolved,
    childrenLiveWith,
  });
  const emergencyFields1Data = getEmergency1Fields({
    emergencyContact1Name,
    emergencyContact1Address,
    emergencyContact1City,
    emergencyContact1Phone,
    emergencyContact1Relationship,
  });
  const emergencyFields2Data = getEmergency2Fields({
    emergencyContact2Name,
    emergencyContact2Address,
    emergencyContact2City,
    emergencyContact2Phone,
    emergencyContact2Relationship,
  });
  const sucideData = getSucideData({
    classifiedCCCMS,
    classifiedCCCMSYear,
    classifiedCCCMSClearedYear,
  });
  const medicineData = getMedicineData({
    medicineName1,
    medicineName2,
    medicineName3,
    medicineCondition1,
    medicineCondition2,
    medicineCondition3,
  });
  const careSectionData = getCareSectionData({
    referral,
    residential,
    programOpt,
  });
  const transitionalData = getProgramTransitionalData({ dateSigned });
  const transitionalTitle = getProgramTransitionalTitle();
  const mappedFieldData = getMappedFieldData({
    participantFieldsData,
    peroleFieldsData,
    addressFieldsData,
    marritalFieldsData,
    childrenFieldsData,
    emergencyFields1Data,
    emergencyFields2Data,
  });

  return (
    <Dialog fullScreen open={open} onClose={setOpen}>
      <PrintButtonHeader handlePrint={handlePrint} handleClose={handleClose} />
      <Grid container ref={componentRef}>
        <HeaderSection episodeID={episodeID} />
        <MappedFieldSection fieldData={mappedFieldData} />
        <SucideSection
          items={sucideData}
          sucideField={historyofSuicideAttemptsPsychIssuesReportedbyPart}
        />
        <MedicationSection items={medicineData} comments={medComments} />
        <SubstanceAndTreatmentSection
          items={substanceData}
          title={substanceTitle}
        />
        <LengthOfTimeAndTimeSpent
          lengthOfTime={LengthofTimeSinceClientLastUsed}
          timeSpent={TimeSpentinInstitutions}
        />
        <SubstanceAndTreatmentSection
          items={historyData}
          title={historyTitle}
        />
        <Grid container pb={1} mt={1} mx={2} spacing={1}>
          <Grid item xs={12}>
            {TeatmentAccomplishmentTitle}
          </Grid>
          <Grid item xs={12} height={32}>
            {treatmentAccomplishments}
          </Grid>
        </Grid>
        <ContinuingCareRecommendation item={careSectionData} />
        <ProgramTransitionalSection
          items={transitionalData}
          title={transitionalTitle}
        />
      </Grid>
    </Dialog>
  );
};

export default ProfA1PdfForm;

ProfA1PdfForm.defaultProps = {
  open: false,
  setOpen: () => {},
};

ProfA1PdfForm.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};
